import React, { useEffect, useState, useCallback } from "react";
import { FaPlus, FaMinus, FaTrash } from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation, Trans } from "react-i18next";
import "react-toastify/dist/ReactToastify.css";
import { useCart } from "../context/CartContext";
import { getShippingConfig, calcShipping } from "../services/shipping";
import "../css/cart.css";

// ─── Delete confirmation modal ────────────────────────────────────────────────
const DeleteModal = ({ productName, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  return (
    <div className="del-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="del-modal">
        <span className="del-modal-icon">🗑️</span>
        <h3>{t("cart.removeItemTitle")}</h3>
        <p>{t("cart.removeItemText")}</p>
        <span className="del-modal-product-name">{productName}</span>
        <div className="del-modal-btns">
          <button className="del-modal-cancel" onClick={onCancel}>{t("cart.keepIt")}</button>
          <button className="del-modal-confirm" onClick={onConfirm}>{t("cart.remove")}</button>
        </div>
      </div>
    </div>
  );
};

const Cart = () => {
  const {
    cartItems, fetchCartItems, authReady, isLoading,
    updateCartItemQuantity, removeFromCart,
  } = useCart();

  const [updatingItems, setUpdatingItems] = useState({});
  const [hasFetched,    setHasFetched]    = useState(false);
  const [deleteModal,   setDeleteModal]   = useState(null);
  const [summary, setSummary] = useState({
    subtotal: 0, discount: 0, shipping: 0,
    promoDiscount: 0, total: 0, totalItems: 0,
  });
  const [shipCfg, setShipCfg] = useState({ fee: 5000, threshold: 50000 });

  const navigate = useNavigate();
  const { t } = useTranslation();

  const computeSummary = useCallback(() => {
    if (cartItems.length === 0) {
      setSummary({ subtotal: 0, discount: 0, shipping: 0, promoDiscount: 0, total: 0, totalItems: 0 });
      return;
    }
    let subtotal = 0, totalItems = 0;
    cartItems.forEach((item) => {
      const product = item.product_id;
      const price = product?.is_on_sale && product?.sale_price != null
        ? product.sale_price
        : product?.sell_price;
      subtotal   += (price || 0) * (item.quantity || 0);
      totalItems += item.quantity || 0;
    });
    const shipping = calcShipping(subtotal, shipCfg);
    setSummary({ subtotal, discount: 0, shipping, promoDiscount: 0, total: subtotal + shipping, totalItems });
  }, [cartItems, shipCfg]);

  useEffect(() => {
    if (authReady && !hasFetched) {
      fetchCartItems().then(() => { setHasFetched(true); computeSummary(); });
    }
  }, [authReady, fetchCartItems, hasFetched, computeSummary]);

  useEffect(() => { getShippingConfig().then(setShipCfg); }, []);
  useEffect(() => { if (hasFetched) computeSummary(); }, [shipCfg, hasFetched, computeSummary]);

  useEffect(() => {
    if (hasFetched) computeSummary();
  }, [hasFetched, cartItems, computeSummary]);

  const updateQuantity = async (productId, operation) => {
    setUpdatingItems(prev => ({ ...prev, [productId]: true }));
    try {
      const item   = cartItems.find(i => (i.product_id._id || i.product_id) === productId);
      const newQty = operation === "increase" ? item.quantity + 1 : item.quantity - 1;
      if (newQty < 1) return;
      await updateCartItemQuantity(productId, newQty);
      computeSummary();
    } catch {
      toast.error(t("cart.maxLimit"));
    } finally {
      setUpdatingItems(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleRemoveItem = (productId, productName) => {
    setDeleteModal({ productId, productName });
  };

  const confirmDelete = async () => {
    const { productId } = deleteModal;
    setDeleteModal(null);
    setUpdatingItems(prev => ({ ...prev, [productId]: true }));
    try {
      await removeFromCart(productId);
      computeSummary();
      toast.success(t("cart.itemRemoved"));
    } catch {
      toast.error(t("cart.removeFailed"));
    } finally {
      setUpdatingItems(prev => ({ ...prev, [productId]: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="cart-page">
        <div className="cart-inner">
          <div className="cart-loading">
            <div className="cart-spinner" />
            <span>{t("cart.loading")}</span>
          </div>
        </div>
      </div>
    );
  }

  // Helper to render a single item — used for both mobile and desktop
  const getItemData = (item) => {
    const productId  = item.product_id?._id || item.product_id;
    const product    = item.product_id || {};
    const price      = product.is_on_sale ? product.sale_price : product.sell_price;
    const totalPrice = (price || 0) * (item.quantity || 0);
    const isUpdating = updatingItems[productId];
    const imageSrc   = product.imageUrl?.startsWith("/")
      ? `${process.env.REACT_APP_API_URL}${product.imageUrl}`
      : product.imageUrl || "/images/default-product.jpg";
    return { productId, product, price, totalPrice, isUpdating, imageSrc };
  };

  return (
    <div className="cart-page">
      <ToastContainer position="bottom-right" autoClose={3000} />

      {deleteModal && (
        <DeleteModal
          productName={deleteModal.productName}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteModal(null)}
        />
      )}

      <div className="cart-inner">
        <h2 className="cart-page-title">{t("cart.title")}</h2>

        {summary.shipping > 0 && cartItems.length > 0 && (
          <div className="free-ship-bar">
            <span>
              <Trans
                i18nKey="cart.freeShip"
                values={{ amount: `₩${Math.max(0, shipCfg.threshold - summary.subtotal).toLocaleString()}` }}
                components={{ b: <strong /> }}
              />
            </span>
            <button onClick={() => navigate("/shop")}>{t("cart.continueShopping")}</button>
          </div>
        )}

        {cartItems.length === 0 ? (
          <div className="empty-cart-wrap">
            <span className="empty-cart-icon">🛒</span>
            <h3>{t("cart.emptyTitle")}</h3>
            <p>{t("cart.emptyText")}</p>
            <Link to="/shop">{t("cart.browseProducts")}</Link>
          </div>
        ) : (
          <div className="cart-layout">

            {/* ─── Cart card ─── */}
            <div className="cart-card">

              {/* ─── Desktop table ─── */}
              <table className="cart-table">
                <thead>
                  <tr>
                    <th>{t("cart.product")}</th>
                    <th>{t("cart.price")}</th>
                    <th>{t("cart.quantity")}</th>
                    <th>{t("cart.total")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {cartItems.map((item, index) => {
                    const { productId, product, price, totalPrice, isUpdating, imageSrc } = getItemData(item);
                    return (
                      <tr key={`${productId}-${index}`} className={isUpdating ? "updating" : ""}>
                        <td>
                          <div className="cart-product-cell">
                            <img
                              src={imageSrc}
                              alt={product.name || "Product"}
                              className="cart-product-img"
                              onError={e => { e.target.onerror = null; e.target.src = "/images/default-product.jpg"; }}
                            />
                            <span className="cart-product-name">{product.name || "Loading..."}</span>
                          </div>
                        </td>
                        <td>₩{price?.toLocaleString() || "0"}</td>
                        <td>
                          <div className="qty-controls">
                            <button className="qty-btn"
                              onClick={() => updateQuantity(productId, "decrease")}
                              disabled={isUpdating || item.quantity <= 1}>
                              <FaMinus />
                            </button>
                            <span className="qty-value">{item.quantity}</span>
                            <button className="qty-btn"
                              onClick={() => updateQuantity(productId, "increase")}
                              disabled={isUpdating}>
                              <FaPlus />
                            </button>
                          </div>
                        </td>
                        <td><strong>₩{totalPrice?.toLocaleString() || "0"}</strong></td>
                        <td>
                          <button className="del-btn"
                            onClick={() => handleRemoveItem(productId, product.name || t("cart.thisItem"))}
                            disabled={isUpdating} title={t("cart.remove")}>
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* ─── Mobile card list ─── */}
              <div className="cart-mobile-list">
                {cartItems.map((item, index) => {
                  const { productId, product, price, totalPrice, isUpdating, imageSrc } = getItemData(item);
                  return (
                    <div
                      key={`m-${productId}-${index}`}
                      className={`cart-mobile-item ${isUpdating ? "updating" : ""}`}
                    >
                      {/* Top row: image + info + delete */}
                      <div className="cart-mobile-top">
                        <img
                          src={imageSrc}
                          alt={product.name || "Product"}
                          className="cart-mobile-img"
                          onError={e => { e.target.onerror = null; e.target.src = "/images/default-product.jpg"; }}
                        />
                        <div className="cart-mobile-info">
                          <div className="cart-mobile-name">{product.name || "Loading..."}</div>
                          <div className="cart-mobile-price">₩{price?.toLocaleString() || "0"} / {t("cart.each")}</div>
                          <div className="cart-mobile-total">₩{totalPrice?.toLocaleString() || "0"}</div>
                        </div>
                        <button
                          className="cart-mobile-del"
                          onClick={() => handleRemoveItem(productId, product.name || t("cart.thisItem"))}
                          disabled={isUpdating}
                          aria-label={t("cart.remove")}
                        >
                          <FaTrash />
                        </button>
                      </div>

                      {/* Bottom row: qty controls */}
                      <div className="cart-mobile-bottom">
                        <span className="cart-mobile-qty-label">{t("cart.quantity")}</span>
                        <div className="qty-controls">
                          <button
                            className="qty-btn"
                            onClick={() => updateQuantity(productId, "decrease")}
                            disabled={isUpdating || item.quantity <= 1}
                          >
                            <FaMinus />
                          </button>
                          <span className="qty-value">{item.quantity}</span>
                          <button
                            className="qty-btn"
                            onClick={() => updateQuantity(productId, "increase")}
                            disabled={isUpdating}
                          >
                            <FaPlus />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ─── Summary card ─── */}
            <div className="summary-card">
              <h3 className="summary-title">{t("cart.orderSummary")}</h3>

              <div className="summary-row">
                <span>{t("cart.subtotal")}</span>
                <span>₩{summary.subtotal.toLocaleString()}</span>
              </div>
              <div className="summary-row">
                <span>{t("cart.shipping")}</span>
                <span>
                  {summary.shipping === 0
                    ? <span className="free-ship">{t("cart.free")}</span>
                    : `₩${summary.shipping.toLocaleString()}`
                  }
                </span>
              </div>
              {summary.discount > 0 && (
                <div className="summary-row">
                  <span>{t("cart.discount")}</span>
                  <span style={{ color: "#1a5c38" }}>-₩{summary.discount.toLocaleString()}</span>
                </div>
              )}
              <div className="summary-row total">
                <span>{t("cart.total")}</span>
                <span>₩{summary.total.toLocaleString()}</span>
              </div>

              <button
                className="checkout-btn"
                onClick={() => {
                  const isGuest = localStorage.getItem("isGuest") === "true";
                  navigate(isGuest ? "/checkoutpage" : "/usercheckoutpage");
                }}
              >
                {t("cart.proceedToCheckout")}
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;